import { useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { clearAdClickAttribution } from "@/lib/ad-attribution";

const COOKIE_CONSENT_KEY = "rm_cookie_consent";
const COOKIE_SETTINGS_KEY = "rm_cookie_settings";

interface CookieSettings {
  necessary: boolean;
  preferences: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

function getCookieSettings(): CookieSettings | null {
  try {
    const stored = localStorage.getItem(COOKIE_SETTINGS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveCookieSettings(settings: CookieSettings) {
  localStorage.setItem(COOKIE_SETTINGS_KEY, JSON.stringify(settings));
  localStorage.setItem(COOKIE_CONSENT_KEY, "true");
}

function applySettings(settings: CookieSettings) {
  if (!settings.marketing) clearAdClickAttribution();
  window.dispatchEvent(
    new CustomEvent("cookieConsentChanged", { detail: settings }),
  );
}

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState(true);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existingConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!existingConsent) {
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    } else {
      const settings = getCookieSettings();
      if (settings) {
        applySettings(settings);
      }
    }
  }, []);

  // Escuchar evento externo para abrir configuración
  useEffect(() => {
    const handler = () => {
      openModal();
    };
    window.addEventListener("openCookieSettings", handler);
    return () => window.removeEventListener("openCookieSettings", handler);
  }, []);

  const openModal = useCallback(() => {
    setShowBanner(false);
    const settings = getCookieSettings();
    if (settings) {
      setPreferences(settings.preferences);
      setAnalytics(settings.analytics);
      setMarketing(settings.marketing);
    }
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const acceptAll = useCallback(() => {
    const settings: CookieSettings = {
      necessary: true,
      preferences: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
    };
    saveCookieSettings(settings);
    setShowBanner(false);
    setShowModal(false);
    applySettings(settings);
  }, []);

  const rejectOptional = useCallback(() => {
    const settings: CookieSettings = {
      necessary: true,
      preferences: false,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
    };
    saveCookieSettings(settings);
    setShowBanner(false);
    setShowModal(false);
    applySettings(settings);
  }, []);

  const saveCurrentSettings = useCallback(() => {
    const settings: CookieSettings = {
      necessary: true,
      preferences,
      analytics,
      marketing,
      timestamp: Date.now(),
    };
    saveCookieSettings(settings);
    setShowBanner(false);
    setShowModal(false);
    applySettings(settings);
  }, [preferences, analytics, marketing]);

  return (
    <>
      {/* Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[9999]">
          <div className="bg-white border-t border-gray-200 shadow-lg">
            <div className="max-w-5xl mx-auto px-4 py-5">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1.5">
                    🍪 Utilizamos cookies
                  </h3>
                  <p className="text-sm text-gray-600">
                    Usamos cookies técnicas necesarias y, si nos das permiso,
                    cookies de preferencias, analíticas y marketing para mejorar
                    tu experiencia. Puedes aceptar todas, rechazar las
                    opcionales o configurar tu preferencia.{" "}
                    <a
                      href="https://reservamanager.com/cookies"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      Política de cookies completa
                    </a>
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2.5 w-full lg:w-auto flex-shrink-0">
                  <button
                    onClick={rejectOptional}
                    className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Rechazar
                  </button>
                  <button
                    onClick={openModal}
                    className="px-5 py-2.5 text-sm font-medium text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                  >
                    Configurar
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Aceptar todo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[9999]">
          <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90dvh] overflow-y-auto relative">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    Configurar cookies
                  </h2>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  Puedes activar o desactivar los distintos tipos de cookies.
                  Las cookies técnicas son necesarias y no se pueden desactivar.
                </p>

                <div className="space-y-4">
                  {/* Técnicas - siempre activas */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          🔧 Técnicas (necesarias)
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Autenticación, seguridad y configuración esencial.
                        </p>
                      </div>
                      <div className="flex items-center flex-shrink-0">
                        <span className="text-xs text-gray-500 mr-2">
                          Siempre activas
                        </span>
                        <div className="w-11 h-6 bg-primary-600 rounded-full relative">
                          <div className="absolute top-0.5 right-0.5 w-5 h-5 bg-white rounded-full" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preferencias */}
                  <CookieToggle
                    label="⚙️ Preferencias"
                    description="Idioma y personalización de la experiencia."
                    checked={preferences}
                    onChange={setPreferences}
                  />

                  {/* Analíticas */}
                  <CookieToggle
                    label="📊 Analíticas"
                    description="Medición de uso para mejorar el servicio."
                    checked={analytics}
                    onChange={setAnalytics}
                  />

                  {/* Marketing */}
                  <CookieToggle
                    label="📢 Marketing"
                    description="Publicidad personalizada."
                    checked={marketing}
                    onChange={setMarketing}
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button
                    onClick={saveCurrentSettings}
                    className="flex-1 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Guardar preferencias
                  </button>
                  <button
                    onClick={acceptAll}
                    className="flex-1 px-5 py-2.5 text-sm font-medium text-primary-600 border border-primary-600 rounded-lg hover:bg-primary-50 transition-colors"
                  >
                    Aceptar todo
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-4 text-center">
                  <a
                    href="https://reservamanager.com/cookies"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Política de cookies
                  </a>{" "}
                  ·{" "}
                  <a
                    href="https://reservamanager.com/privacidad"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Política de privacidad
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CookieToggle({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <div className="p-4 bg-gray-50 rounded-lg">
      <div className="flex justify-between items-center">
        <div className="mr-4">
          <h3 className="font-semibold text-gray-900">{label}</h3>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600" />
        </label>
      </div>
    </div>
  );
}
